import React, { useState, useEffect, useCallback } from "react";
import { adminAxios, imgUrl } from "./adminApi";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../components/ui/alert-dialog";
import { useToast } from "../hooks/use-toast";
import { Plus, Pencil, Trash2, RefreshCw, Upload, Loader2 } from "lucide-react";

const ImageField = ({ value, onChange }) => {
  const [uploading, setUploading] = useState(false);
  const { toast } = useToast();
  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await adminAxios().post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(res.data.url);
      toast({ title: "Image uploaded" });
    } catch { toast({ title: "Upload failed" }); } finally { setUploading(false); }
  };
  return (
    <div className="flex items-center gap-3">
      {value ? <img src={imgUrl(value)} alt="" className="w-16 h-16 rounded-lg object-cover ring-1 ring-black/5" /> : <div className="w-16 h-16 rounded-lg bg-secondary flex items-center justify-center text-gray-400"><Upload className="w-5 h-5" /></div>}
      <label className="cursor-pointer">
        <input type="file" accept="image/*" className="hidden" onChange={handle} />
        <span className="inline-flex items-center gap-1.5 text-sm border rounded-lg px-3 py-2 hover:bg-secondary">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} {value ? "Change" : "Upload"} Image
        </span>
      </label>
    </div>
  );
};

const MultiSelect = ({ value = [], options, onChange }) => {
  const selected = Array.isArray(value) ? value : [];
  const toggle = (name) => onChange(selected.includes(name) ? selected.filter((s) => s !== name) : [...selected, name]);
  const total = options.filter((o) => selected.includes(o.name)).reduce((s, o) => s + (o.price || 0), 0);
  return (
    <div className="border rounded-lg p-3 max-h-52 overflow-y-auto space-y-1.5">
      {options.map((o) => (
        <label key={o.id || o.name} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-secondary rounded px-1.5 py-1">
          <Checkbox checked={selected.includes(o.name)} onCheckedChange={() => toggle(o.name)} />
          <span className="flex-1">{o.name}</span>
          <span className="text-gray-500">₹{o.price}</span>
        </label>
      ))}
      {selected.length > 0 && <div className="text-xs text-primary font-medium pt-2 border-t mt-2">Selected total: ₹{total} ({selected.length} items)</div>}
    </div>
  );
};

const ResourceManager = ({ title, endpoint, fields, empty }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [dynOptions, setDynOptions] = useState({});
  const { toast } = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminAxios().get(`/admin/${endpoint}`);
      setItems(res.data);
    } catch { toast({ title: "Failed to load" }); } finally { setLoading(false); }
  }, [endpoint]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  // fetch dynamic options for select/multiselect fields
  useEffect(() => {
    fields.forEach(async (f) => {
      if (f.optionsEndpoint && !dynOptions[f.optionsEndpoint]) {
        try {
          const res = await adminAxios().get(`/admin/${f.optionsEndpoint}`);
          setDynOptions((p) => ({ ...p, [f.optionsEndpoint]: res.data }));
        } catch {}
      }
    });
  }, [fields]); // eslint-disable-line

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...empty, ...it }); setOpen(true); };

  const save = async () => {
    try {
      const payload = { ...form };
      fields.forEach((f) => { if (f.type === "number") payload[f.key] = Number(payload[f.key] || 0); });
      if (payload.services !== undefined && typeof payload.services === "string")
        payload.services = payload.services.split(",").map((s) => s.trim()).filter(Boolean);
      if (editing) await adminAxios().put(`/admin/${endpoint}/${editing.id}`, payload);
      else await adminAxios().post(`/admin/${endpoint}`, payload);
      toast({ title: editing ? "Updated" : "Created" });
      setOpen(false); load();
    } catch (e) { toast({ title: "Save failed", description: e?.response?.data?.detail || "" }); }
  };

  const remove = async (id) => {
    try { await adminAxios().delete(`/admin/${endpoint}/${id}`); toast({ title: "Deleted" }); load(); }
    catch { toast({ title: "Delete failed" }); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div><h2 className="font-display text-2xl font-bold text-gray-900">{title}</h2><p className="text-sm text-gray-500">{items.length} item(s)</p></div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load}><RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /></Button>
          <Button size="sm" onClick={openNew} className="bg-primary hover:bg-primary/90"><Plus className="w-4 h-4 mr-1" /> Add</Button>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((it) => (
          <div key={it.id} className="bg-white rounded-xl ring-1 ring-black/5 shadow-sm overflow-hidden flex flex-col">
            {it.image ? <img src={imgUrl(it.image)} alt="" className="h-32 w-full object-cover" /> : null}
            <div className="p-4 flex-1 flex flex-col">
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-semibold text-gray-900">{it.name || it.title}</h3>
                {"active" in it && <Badge className={it.active ? "bg-green-100 text-green-700" : "bg-gray-200 text-gray-600"}>{it.active ? "Active" : "Off"}</Badge>}
              </div>
              {it.code && <div className="text-xs font-mono bg-secondary inline-block px-2 py-0.5 rounded mt-1 w-fit">{it.code}</div>}
              <p className="text-sm text-gray-500 mt-1 line-clamp-2">{it.description}</p>
              {Array.isArray(it.services) && it.services.length > 0 && <p className="text-xs text-gray-400 mt-1">Includes: {it.services.join(", ")}</p>}
              <div className="flex flex-wrap gap-2 mt-2 text-sm text-gray-700">
                {it.price !== undefined && <span className="font-bold text-primary">₹{it.price}</span>}
                {it.original_price ? <span className="line-through text-gray-400">₹{it.original_price}</span> : null}
                {it.duration && <span>· {it.duration}</span>}
                {it.category && <span>· {it.category}</span>}
                {it.discount_value !== undefined && <span>{it.discount_value}{it.discount_type === "percent" ? "%" : "₹"} off</span>}
                {it.phone && <span>· {it.phone}</span>}
                {it.location && <span>· {it.location}</span>}
              </div>
              <div className="flex gap-2 mt-3 pt-3 border-t border-gray-100">
                <Button variant="outline" size="sm" className="flex-1" onClick={() => openEdit(it)}><Pencil className="w-3.5 h-3.5 mr-1" /> Edit</Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild><Button variant="ghost" size="icon" className="text-gray-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></Button></AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader><AlertDialogTitle>Delete this item?</AlertDialogTitle><AlertDialogDescription>This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
                    <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction className="bg-red-500 hover:bg-red-600" onClick={() => remove(it.id)}>Delete</AlertDialogAction></AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit" : "Add"} {title.replace(/s$/, "")}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            {fields.map((f) => {
              const opts = f.optionsEndpoint ? (dynOptions[f.optionsEndpoint] || []) : (f.options || []);
              return (
                <div key={f.key} className="space-y-1.5">
                  <Label>{f.label}</Label>
                  {f.type === "textarea" ? (
                    <Textarea value={form[f.key] ?? ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} />
                  ) : f.type === "image" ? (
                    <ImageField value={form[f.key]} onChange={(v) => setForm({ ...form, [f.key]: v })} />
                  ) : f.type === "multiselect" ? (
                    <MultiSelect value={form[f.key]} options={opts} onChange={(v) => {
                      const upd = { ...form, [f.key]: v };
                      if (f.autoPriceField) upd[f.autoPriceField] = opts.filter((o) => v.includes(o.name)).reduce((s, o) => s + (o.price || 0), 0);
                      setForm(upd);
                    }} />
                  ) : f.type === "select" ? (
                    <Select value={form[f.key]} onValueChange={(v) => setForm({ ...form, [f.key]: v })}>
                      <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{(f.optionsEndpoint ? opts.map((o) => o.name) : opts).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : f.type === "switch" ? (
                    <div className="flex items-center gap-2"><Switch checked={!!form[f.key]} onCheckedChange={(v) => setForm({ ...form, [f.key]: v })} /><span className="text-sm text-gray-500">{form[f.key] ? "Active" : "Inactive"}</span></div>
                  ) : (
                    <Input type={f.type === "number" ? "number" : "text"} value={form[f.key] ?? ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} placeholder={f.placeholder || ""} />
                  )}
                </div>
              );
            })}
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-primary hover:bg-primary/90" onClick={save}>{editing ? "Save changes" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ResourceManager;
